import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { colors, TENANT_LOGO, TENANT_NAME } from '../utils/constants';
import LockResetIcon from '@mui/icons-material/LockReset';
import { api } from '../utils/api';
import toast from 'react-hot-toast';

const ForcePasswordChangePage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      await api.patch('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });

      toast.success('Password changed successfully!');
      
      // Refresh user data and redirect to home
      await checkAuth();
      navigate('/');
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to change password';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Redirect if user doesn't have temporary password
  React.useEffect(() => {
    if (user && !user.is_temporary_password) {
      navigate('/');
    }
  }, [user, navigate]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <img 
            src={TENANT_LOGO} 
            alt={TENANT_NAME} 
            style={{ 
              height: 80, 
              width: 80,
              marginBottom: 16,
            }} 
          />
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              color: colors.primary,
              letterSpacing: '-1px'
            }}
          >
            {TENANT_NAME}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Storefront
          </Typography>
        </Box>

        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            width: '100%',
            borderRadius: 3,
            boxShadow: `0 8px 32px rgba(255, 0, 144, 0.15)`,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockResetIcon 
              sx={{ 
                fontSize: 48, 
                color: colors.primary,
                mb: 2
              }} 
            />
          </Box>

          <Typography variant="h4" component="h2" gutterBottom align="center">
            Change Password Required
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            You must change your temporary password before accessing the application.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              helperText="Password must be at least 6 characters long"
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="outlined"
              size="large"
              disabled={loading}
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                borderColor: colors.primary,
                color: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primary,
                  color: '#fff',
                  borderColor: colors.primary,
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Change Password'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForcePasswordChangePage;